

import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const EMPLOYER_STATUSES = ['potential', 'contacted', 'interested', 'invited', 'claimed', 'verified', 'active_employer', 'rejected'];
const VERIFICATION_STATUSES = ['unverified', 'phone_verified', 'website_verified', 'owner_verified', 'admin_verified'];

const STATUS_STYLES = {
  potential: 'bg-slate-100 text-slate-600',
  contacted: 'bg-orange-100 text-orange-700',
  interested: 'bg-purple-100 text-purple-700',
  invited: 'bg-yellow-100 text-yellow-700',
  claimed: 'bg-teal-100 text-teal-700',
  verified: 'bg-green-100 text-green-700',
  active_employer: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  potential: 'Potential',
  contacted: 'Contacted',
  interested: 'Interested',
  invited: 'Invited',
  claimed: 'Claimed',
  verified: 'Verified',
  active_employer: 'Active Employer',
  rejected: 'Rejected',
};

const VERIFICATION_STYLES = {
  unverified: 'bg-gray-100 text-gray-500',
  phone_verified: 'bg-yellow-100 text-yellow-700',
  website_verified: 'bg-yellow-100 text-yellow-700',
  owner_verified: 'bg-blue-100 hover:text-purple-600',
  admin_verified: 'bg-green-100 text-green-700',
};

const VERIFICATION_LABELS = {
  unverified: 'Unverified',
  phone_verified: 'Phone Verified',
  website_verified: 'Website Verified',
  owner_verified: 'Owner Verified',
  admin_verified: 'Admin Verified',
};

const CONTACT_LOOKUP_STYLES = {
  found: 'bg-green-100 text-green-700',
  website_only: 'bg-yellow-100 text-yellow-700',
  not_found: 'bg-gray-100 text-gray-500',
  pending: 'bg-gray-50 text-gray-400',
};

const CONTACT_LOOKUP_LABELS = {
  found: 'Contact found',
  website_only: 'Website only',
  not_found: 'No contact found',
  pending: 'No contact on file',
};


const FOLLOW_UP_STYLES = {
  overdue: 'bg-red-100 text-red-700',
  today: 'bg-orange-100 text-orange-700',
  upcoming: 'bg-blue-50 hover:text-purple-600',
  done: 'bg-green-100 text-green-700',
};

const FOLLOW_UP_LABELS = {
  overdue: 'Overdue',
  today: 'Due today',
  upcoming: 'Upcoming',
  done: 'Done',
};

function FollowUpBadge({ followUp }) {
  if (!followUp) return null;
  const style = FOLLOW_UP_STYLES[followUp.status] || FOLLOW_UP_STYLES.upcoming;
  const label = FOLLOW_UP_LABELS[followUp.status] || 'Follow-up';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${style}`}>
      <ClockIcon />
      {label}: {formatDate(followUp.due_at)}
    </span>
  );
}

const FIELD_LABELS = {
  employer_status: 'Employer status',
  verification_status: 'Verification status',
};

const ContactLookupBadge = ({ status }) => {
  const key = status || 'pending';
  return (
    <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${CONTACT_LOOKUP_STYLES[key] || CONTACT_LOOKUP_STYLES.pending}`}>
      {CONTACT_LOOKUP_LABELS[key] || CONTACT_LOOKUP_LABELS.pending}
    </span>
  );
};

const quickSearchUrl = (name, engine) => {
  const q = encodeURIComponent(name || '');
  return engine === 'linkedin'
    ? `https://www.linkedin.com/search/results/companies/?keywords=${q}`
    : `https://www.google.com/search?q=${q}+contact`;
};

function EditableStatusBadge({ value, options, styles, labels, onChange, disabled }) {
  const key = value || options[0];
  const colorClass = styles[key] || 'bg-gray-100 text-gray-600';

  return (
    <div className="relative inline-block">
      <select
        value={key}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`appearance-none text-xs font-semibold pl-2.5 pr-6 py-1 rounded-full whitespace-nowrap cursor-pointer border-0 focus:outline-none focus:ring-2focus:ring-purple-400 ${colorClass} ${disabled ? 'opacity-60 cursor-wait' : ''}`}
      >
        {options.map((opt) => (
          <option key={opt} value={opt} className="bg-white text-gray-800">
            {labels[opt] || opt}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2">
        <ChevronIcon open={false} />
      </span>
    </div>
  );
}

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatFieldValue = (field, value) => {
  if (!value) return '—';
  if (field === 'employer_status') return STATUS_LABELS[value] || value;
  if (field === 'verification_status') return VERIFICATION_LABELS[value] || value;
  return value;
};

const PIPELINE_STAGES = ['potential', 'contacted', 'interested', 'invited', 'claimed', 'verified', 'active_employer'];

function EmployerPipeline({ currentStatus }) {
  const currentIndex = PIPELINE_STAGES.indexOf(currentStatus);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {PIPELINE_STAGES.map((stage, i) => {
        const reached = currentIndex >= 0 && i <= currentIndex;
        return (
          <div key={stage} className="flex items-center gap-1.5">
            <span className={`text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap ${reached ? STATUS_STYLES[stage] : 'bg-gray-50 text-gray-300'}`}>
              {STATUS_LABELS[stage]}
            </span>
            {i < PIPELINE_STAGES.length - 1 && <span className="text-gray-300 text-xs">→</span>}
          </div>
        );
      })}
    </div>
  );
}

function AuditLogTimeline({ businessId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/businesses/${businessId}/audit-logs`)
      .then(({ data }) => { if (!cancelled) setLogs(data || []); })
      .catch(() => { if (!cancelled) setLogs([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [businessId]);

  if (loading) {
    return <div className="text-xs text-gray-400 py-2">Loading history…</div>;
  }

  if (logs.length === 0) {
    return <div className="text-xs text-gray-400 py-2">No changes recorded yet.</div>;
  }

  return (
    <div className="space-y-3">
      {logs.map((log) => (
        <div key={log.id} className="text-sm border-l-2 border-gray-100 pl-3">
          <div className="text-gray-700">
            <span className="font-medium">{FIELD_LABELS[log.field_name] || log.field_name}</span>
            {' changed from '}
            <span className="text-gray-500">{formatFieldValue(log.field_name, log.old_value)}</span>
            {' to '}
            <span className="font-medium">{formatFieldValue(log.field_name, log.new_value)}</span>
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {formatDateTime(log.created_at)}{log.user?.name ? ` · ${log.user.name}` : ''}
          </div>
        </div>
      ))}
    </div>
  );
}

function BusinessDetailDrawer({ businessId, onClose, onStatusChange, onSchedule, onComplete, onDelete }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingField, setSavingField] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get(`/admin/businesses/${businessId}`)
      .then(({ data }) => { if (!cancelled) setDetail(data); })
      .catch(() => { if (!cancelled) setError('Failed to load business details.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [businessId]);

  const handleChange = async (field, value) => {
    setSavingField(field);
    try {
      await onStatusChange(businessId, field, value);
      setDetail((prev) => (prev ? { ...prev, [field]: value } : prev));
      setHistoryKey((k) => k + 1);
    } finally {
      setSavingField(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <CloseIcon />
        </button>

        {loading && <div className="text-sm text-gray-400 py-12 text-center">Loading…</div>}
        {error && <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mt-8">{error}</div>}

        {detail && !loading && (
          <div className="mt-2">
            <h2 className="text-lg font-bold  bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent pr-8">{detail.business_name}</h2>
            <div className="mt-1 text-xs text-gray-500 font-mono">{detail.registration_number || '—'}</div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <EditableStatusBadge
                value={detail.employer_status}
                options={EMPLOYER_STATUSES}
                styles={STATUS_STYLES}
                labels={STATUS_LABELS}
                disabled={savingField === 'employer_status'}
                onChange={(value) => handleChange('employer_status', value)}
              />
              <EditableStatusBadge
                value={detail.verification_status}
                options={VERIFICATION_STATUSES}
                styles={VERIFICATION_STYLES}
                labels={VERIFICATION_LABELS}
                disabled={savingField === 'verification_status'}
                onChange={(value) => handleChange('verification_status', value)}
              />
            </div>

            <div className="mt-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Employer Pipeline</h3>
              <EmployerPipeline currentStatus={detail.employer_status} />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3 text-sm">
              <div>
                <div className="text-xs text-gray-400">Address</div>
                <div className="text-gray-700">{detail.address || '—'}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-400">Phone</div>
                  <div className="text-gray-700">{detail.phone || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Email</div>
                  <div className="text-gray-700 truncate">{detail.email || '—'}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-400">Website</div>
                  <div className="text-gray-700 truncate">{detail.website || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Pincode</div>
                  <div className="text-gray-700">{detail.pincode || '—'}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-400">State</div>
                  <div className="text-gray-700">{detail.state?.name || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Category</div>
                  <div className="text-gray-700">{detail.category?.name || '—'}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-400">Created</div>
                  <div className="text-gray-700">{formatDate(detail.created_at)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Business Status</div>
                  <div className="text-gray-700 capitalize">{detail.business_status || '—'}</div>
                </div>
              </div>
              {detail.claimed_at && (
                <div>
                  <div className="text-xs text-gray-400">Claimed At</div>
                  <div className="text-gray-700">{formatDateTime(detail.claimed_at)}</div>
                </div>
              )}
              {detail.verified_at && (
                <div>
                  <div className="text-xs text-gray-400">Verified At</div>
                  <div className="text-gray-700">{formatDateTime(detail.verified_at)}</div>
                </div>
              )}
            </div>

            {detail.sources && detail.sources.length > 0 && (
              <div className="mt-6">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Source</h3>
                {detail.sources.map((s) => (
                  <div key={s.id} className="text-sm text-gray-700">
                    {s.source_type} {s.source_name ? `— ${s.source_name}` : ''}
                  </div>
                ))}
              </div>
            )}

            {detail.contacts && detail.contacts.length > 0 && (
              <div className="mt-6">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Additional Contacts</h3>
                {detail.contacts.map((c) => (
                  <div key={c.id} className="text-sm text-gray-700">
                    {c.contact_type}: {c.contact_value}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Change History</h3>
              <AuditLogTimeline key={historyKey} businessId={businessId} />
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Follow-ups</h3>
                <button
                  type="button"
                  onClick={() => onSchedule(detail)}
                  className="text-xs font-medium  text-purple-600 hover:underline"
                >
                  + Schedule
                </button>
              </div>

              {(!detail.follow_ups || detail.follow_ups.length === 0) && (
                <div className="text-xs text-gray-400">No follow-ups scheduled.</div>
              )}

              <div className="space-y-2">
                {(detail.follow_ups || []).map((f) => (
                  <div key={f.id} className="border border-gray-100 rounded-lg px-3 py-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <FollowUpBadge followUp={f} />
                        <div className="text-sm text-gray-700 mt-1">{f.note}</div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {f.creator?.name ? `Added by ${f.creator.name}` : ''}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {!f.completed_at && (
                          <button
                            type="button"
                            onClick={() => onComplete(f.id)}
                            className="text-xs font-medium text-green-600 hover:underline whitespace-nowrap"
                          >
                            Mark done
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => onDelete(f.id)}
                          className="text-xs font-medium text-red-600 hover:underline whitespace-nowrap"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BusinessRow({ b, checked, onToggle, onStatusChange, onOpenDetail, onEdit, onDelete, allowDelete, onOpenEmailThread, onScheduleFollowUp }) {
  const [showLookupLinks, setShowLookupLinks] = useState(false);
  const [savingField, setSavingField] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const hasContact = Boolean(b.phone || b.email);

  const handleDeleteClick = async () => {
    if (!window.confirm(`Delete "${b.business_name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await onDelete(b.id);
    } finally {
      setDeleting(false);
    }
  };

  const handleChange = async (field, value) => {
    setSavingField(field);
    try {
      await onStatusChange(b.id, field, value);
    } finally {
      setSavingField(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-purple-50/20 transition-colors">
      <div className="flex items-start pt-1 shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(b.id)}
          className="w-4 h-4 rounded border-gray-300 hover:text-purple-600focus:ring-purple-400"
          aria-label={`Select ${b.business_name}`}
        />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <button
            type="button"
            onClick={() => onOpenDetail(b.id)}
            className="font-bold  bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent truncate hover:underline hover:hover:text-purple-600 text-left"
          >
            {b.business_name}
          </button>
          <EditableStatusBadge
            value={b.employer_status}
            options={EMPLOYER_STATUSES}
            styles={STATUS_STYLES}
            labels={STATUS_LABELS}
            disabled={savingField === 'employer_status'}
            onChange={(value) => handleChange('employer_status', value)}
          />
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
          <span className="font-mono">{b.registration_number || '—'}</span>
          <span>·</span>
          <span>{b.state?.name || 'Unknown state'}</span>
          <span>·</span>
          <span>{formatDate(b.created_at)}</span>
        </div>
        {b.address && (
          <div className="mt-1 text-xs text-gray-400 truncate max-w-xl">{b.address}</div>
        )}
        {(b.phone || b.email || b.website) && (
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
            {b.phone && <span>{b.phone}</span>}
            {b.email && <span className="truncate">{b.email}</span>}
            {b.website && <span className="truncate">{b.website}</span>}
          </div>
        )}

        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          {!hasContact && <ContactLookupBadge status={b.contact_lookup_status} />}

          {!hasContact && (
            <button type="button" onClick={() => setShowLookupLinks((v) => !v)} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 hover:text-purple-600 hover:bg-blue-100">
              Find contact
              <ChevronIcon open={showLookupLinks} />
            </button>
          )}
        </div>

        {!hasContact && showLookupLinks && (
          <div className="mt-1.5 flex flex-wrap items-center gap-2 pl-0.5">
            <a href={quickSearchUrl(b.business_name, 'google')} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 underline decoration-gray-300 hover:hover:text-purple-600">
              Search Google
            </a>
            <span className="text-gray-300 text-xs">·</span>
            <a href={quickSearchUrl(b.business_name, 'linkedin')} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 underline decoration-gray-300 hover:hover:text-purple-600">
              Search LinkedIn
            </a>
          </div>
        )}
      </div>

           <div className="flex items-center gap-2 shrink-0">
        <EditableStatusBadge
          value={b.verification_status}
          options={VERIFICATION_STATUSES}
          styles={VERIFICATION_STYLES}
          labels={VERIFICATION_LABELS}
          disabled={savingField === 'verification_status'}
          onChange={(value) => handleChange('verification_status', value)}
        />
        
        {b.next_follow_up && <FollowUpBadge followUp={b.next_follow_up} />}
        
        <button
          type="button"
          onClick={() => onScheduleFollowUp(b)}
          className="text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 px-2.5 py-1 rounded-full whitespace-nowrap inline-flex items-center gap-1"
        >
          <ClockIcon /> Follow-up
        </button>
        <button
          type="button"
          onClick={() => onOpenEmailThread(b)}
          className="text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 px-2.5 py-1 rounded-full whitespace-nowrap inline-flex items-center gap-1"
        >
          <MailIcon /> Email
        </button>

        <button
          type="button"
          onClick={() => onEdit(b)}
          className="text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-full whitespace-nowrap"
        >
          Edit
        </button>
        
        {allowDelete && (
          <button
            type="button"
            onClick={handleDeleteClick}
            disabled={deleting}
            className="text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 px-2.5 py-1 rounded-full whitespace-nowrap disabled:opacity-50"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        )}
      </div>
    </div>
  );
}

function EditBusinessModal({ business, saving, onClose, onSave }) {
  const [form, setForm] = useState({
    business_name: business.business_name || '',
    phone: business.phone || '',
    email: business.email || '',
    website: business.website || '',
    address: business.address || '',
    pincode: business.pincode || '',
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim()) {
      setError('Business name is required.');
      return;
    }
    setError('');
    try {
      await onSave(business.id, form);
    } catch (err) {
      console.error('Failed to update business', err);
      setError(err.response?.data?.message || 'Failed to save changes. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className=" bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold  bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Edit Business</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <CloseIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Business Name</label>
            <input
              type="text"
              value={form.business_name}
              onChange={(e) => setForm((f) => ({ ...f, business_name: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2focus:ring-purple-400"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
              <input
                type="text"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2focus:ring-purple-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2focus:ring-purple-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Website</label>
            <input
              type="text"
              value={form.website}
              onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2focus:ring-purple-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Address</label>
            <textarea
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2focus:ring-purple-400 resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Pincode</label>
            <input
              type="text"
              value={form.pincode}
              onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2focus:ring-purple-400"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function ScheduleFollowUpModal({ business, onClose, onSave, saving }) {
  const [dueDate, setDueDate] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!dueDate) {
      setError('Please pick a follow-up date.');
      return;
    }
    if (!note.trim()) {
      setError('Please add a short note for what to follow up about.');
      return;
    }
    setError('');
    try {
      await onSave(business.id, { note: note.trim(), due_at: dueDate });
    } catch (err) {
      console.error('Failed to schedule follow-up', err);
      setError(err.response?.data?.message || 'Failed to schedule follow-up. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className=" bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold  bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Schedule follow-up</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <CloseIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          <p className="text-sm text-gray-500">{business.business_name}</p>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Follow-up date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2focus:ring-purple-400"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="e.g. Call back about job posting pricing"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2focus:ring-purple-400 resize-none"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ComposeEmailModal({ recipientCount, templates, onClose, onSend, sending }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const handleTemplateChange = (e) => {
    const id = e.target.value;
    setSelectedTemplateId(id);
    const tpl = templates.find((t) => String(t.id) === id);
    if (tpl) {
      setSubject(tpl.subject);
      setBody(tpl.body);
    }
  };

  const canSend = subject.trim().length > 0 && body.trim().length > 0 && !sending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className=" bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold  bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
            Email {recipientCount} {recipientCount === 1 ? 'business' : 'businesses'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <CloseIcon />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Template (optional)</label>
            <select
              value={selectedTemplateId}
              onChange={handleTemplateChange}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2focus:ring-purple-400 bg-white"
            >
              <option value="">Choose a template…</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. New job opportunity on PlanB Jobs"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2focus:ring-purple-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              placeholder="Write your message here. Basic formatting like <strong> and <p> tags is supported."
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2focus:ring-purple-400 resize-none"
            />
            <p className="mt-1 text-xs text-gray-400">
              This will be wrapped in PlanB Jobs' branded email template automatically.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSend(subject, body)}
            disabled={!canSend}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {sending ? 'Sending…' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TemplateManagerModal({ templates, saving, onClose, onCreate, onUpdate, onDelete }) {
  const [mode, setMode] = useState('list'); // 'list' | 'create' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', subject: '', body: '' });
  const [error, setError] = useState('');

  const resetForm = () => setForm({ name: '', subject: '', body: '' });

  const startCreate = () => {
    resetForm();
    setError('');
    setMode('create');
  };

  const startEdit = (tpl) => {
    setForm({ name: tpl.name, subject: tpl.subject, body: tpl.body });
    setEditingId(tpl.id);
    setError('');
    setMode('edit');
  };

  const cancelForm = () => {
    setMode('list');
    setEditingId(null);
    resetForm();
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
      setError('All fields are required.');
      return;
    }
    setError('');
    try {
      if (mode === 'edit' && editingId) {
        await onUpdate(editingId, form);
      } else {
        await onCreate(form);
      }
      cancelForm();
    } catch (err) {
      console.error('Failed to save template', err);
      setError(err.response?.data?.message || 'Failed to save template. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    setError('');
    try {
      await onDelete(id);
    } catch (err) {
      console.error('Failed to delete template', err);
      setError('Failed to delete template. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className=" bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold  bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Email Templates</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <CloseIcon />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1">
          {error && (
            <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {mode === 'list' && (
            <>
              <button
                type="button"
                onClick={startCreate}
                className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90"
              >
                + New Template
              </button>

              {templates.length === 0 && (
                <div className="text-sm text-gray-400 py-8 text-center">No templates yet. Create one to get started.</div>
              )}

              <div className="space-y-2">
                {templates.map((t) => (
                  <div key={t.id} className="border border-gray-100 rounded-lg px-3 py-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm  bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent truncate">{t.name}</div>
                        <div className="text-xs text-gray-500 truncate">{t.subject}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => startEdit(t)}
                          className="text-xs font-medium  text-purple-600 hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(t.id)}
                          className="text-xs font-medium text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {(mode === 'create' || mode === 'edit') && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Template Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Job Posting Invitation"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2focus:ring-purple-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  placeholder="e.g. Post your job openings on PlanB Jobs"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2focus:ring-purple-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Body</label>
                <textarea
                  value={form.body}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  rows={6}
                  placeholder="Write the template message here."
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2focus:ring-purple-400 resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={cancelForm}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : mode === 'edit' ? 'Save Changes' : 'Create Template'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function SendResultsModal({ result, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className=" bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold  bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Send Results</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <CloseIcon />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm font-semibold text-green-700 bg-green-50 px-3 py-1.5 rounded-lg">
              {result.sent} sent
            </span>
            {result.skipped.length > 0 && (
              <span className="text-sm font-semibold text-red-700 bg-red-50 px-3 py-1.5 rounded-lg">
                {result.skipped.length} failed
              </span>
            )}
          </div>

          {result.skipped.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-gray-500 mb-2">Failed to send:</p>
              {result.skipped.map((s) => (
                <div key={s.id} className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-gray-50">
                  <span className="text-gray-700 truncate">{s.name}</span>
                  <span className="text-xs text-red-600 whitespace-nowrap ml-2">{s.reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function EmailChatDrawer({ business, onClose }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyBody, setReplyBody] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.get(`/admin/businesses/${business.id}/emails`)
      .then(({ data }) => {
        if (cancelled) return;
        setEmails(data);
        // Default the reply subject to "Re: <last subject>" so the thread
        // stays visually connected, same convention as the user's own replies.
        const lastSubject = [...data].reverse().find((e) => e.subject)?.subject;
        if (lastSubject) {
          setReplySubject(lastSubject.startsWith('Re:') ? lastSubject : `Re: ${lastSubject}`);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [business.id]);

  const canSend = replyBody.trim().length > 0 && replySubject.trim().length > 0 && !sending;

  const handleSendReply = async () => {
    if (!canSend) return;
    setSending(true);
    setSendError('');
    try {
      const { data } = await api.post(`/admin/businesses/${business.id}/reply`, {
        subject: replySubject,
        body: replyBody,
      });
      setEmails((prev) => [...prev, data]);
      setReplyBody('');
    } catch (err) {
      console.error('Failed to send reply', err);
      setSendError(err.response?.data?.message || 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    // Cmd/Ctrl + Enter sends, plain Enter makes a new line
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSendReply();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white w-full max-w-lg h-full flex flex-col shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold  bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">{business.business_name}</h2>
            <p className="text-xs text-gray-400">{business.email}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><CloseIcon /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-gray-50">
          {loading && <div className="text-sm text-gray-400 text-center py-8">Loading…</div>}
          {!loading && emails.length === 0 && (
            <div className="text-sm text-gray-400 text-center py-8">No emails yet.</div>
          )}
          {!loading && emails.map((e) => (
            <div key={e.id} className={`flex ${e.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-xl px-4 py-2.5 text-sm ${
                e.direction === e.direction === 'outbound' ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white' : 'bg-white border border-gray-200 text-gray-700'
              }`}>
                {e.subject && <div className="font-semibold text-xs mb-1 opacity-90">{e.subject}</div>}
                <div dangerouslySetInnerHTML={{ __html: e.body }} />
                <div className={`text-[10px] mt-1.5 ${e.direction === 'outbound' ? 'text-blue-100' : 'text-gray-400'}`}>
                  {formatDateTime(e.created_at)}{e.sender?.name ? ` · ${e.sender.name}` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>

        {!loading && business.email && (
          <div className="border-t border-gray-100 px-5 py-3 bg-white">
            {sendError && (
              <div className="mb-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {sendError}
              </div>
            )}
            <input
              type="text"
              value={replySubject}
              onChange={(e) => setReplySubject(e.target.value)}
              placeholder="Subject"
              className="w-full mb-2 px-3 py-1.5 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2focus:ring-purple-400"
            />
            <div className="flex items-end gap-2">
              <textarea
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Write a reply… (Ctrl/Cmd + Enter to send)"
                rows={2}
                className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2focus:ring-purple-400 resize-none"
              />
              <button
                type="button"
                onClick={handleSendReply}
                disabled={!canSend}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              >
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        )}

        {!loading && !business.email && (
          <div className="border-t border-gray-100 px-5 py-3 bg-gray-50 text-xs text-gray-400 text-center">
            No email on file for this business — can't reply.
          </div>
        )}
      </div>
    </div>
  );
}

export default function BusinessesPanel({ allowTemplateManagement = true, allowDelete = true }) {
  const [businesses, setBusinesses] = useState([]);
  const [states, setStates] = useState([]);
  const [search, setSearch] = useState('');
  const [stateId, setStateId] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);

  const [selectedIds, setSelectedIds] = useState([]);
  const [showCompose, setShowCompose] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendError, setSendError] = useState('');

  const [templates, setTemplates] = useState([]);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [businessSaving, setBusinessSaving] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
    const [emailThreadBusiness, setEmailThreadBusiness] = useState(null);
    const [followUpBusiness, setFollowUpBusiness] = useState(null);
    const [followUpSaving, setFollowUpSaving] = useState(false);
    const [followUpDetailKey, setFollowUpDetailKey] = useState(0);

  useEffect(() => {
    api.get('/admin/states')
      .then(({ data }) => setStates(data))
      .catch((err) => console.error('Failed to load states', err));
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/email-templates');
      setTemplates(data || []);
    } catch (err) {
      console.error('Failed to load email templates', err);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const fetchBusinesses = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/businesses', {
        params: {
          search: search || undefined,
          state_id: stateId || undefined,
          employer_status: status || undefined,
          page,
          per_page: 25,
        },
      });
      setBusinesses(data.data || []);
      setMeta({ total: data.total, last_page: data.last_page });
    } catch (err) {
      console.error('Failed to load businesses', err);
    } finally {
      setLoading(false);
    }
  }, [search, stateId, status, page]);

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  // Selection resets when filters/page change, since the visible business
  // list changes underneath it and stale IDs from a previous page would be
  // confusing to carry forward silently.
  useEffect(() => {
    setSelectedIds([]);
  }, [search, stateId, status, page]);

  const handleStatusChange = async (businessId, field, value) => {
    setErrorMsg('');
    const previous = businesses.find((b) => b.id === businessId);
    setBusinesses((prev) => prev.map((b) => (b.id === businessId ? { ...b, [field]: value } : b)));

    try {
      await api.put(`/admin/businesses/${businessId}`, { [field]: value });
    } catch (err) {
      console.error('Failed to update status', err);
      setErrorMsg('Failed to update status. Please try again.');
      if (previous) {
        setBusinesses((prev) => prev.map((b) => (b.id === businessId ? previous : b)));
      }
    }
  };

  const handleSaveBusinessEdit = async (businessId, form) => {
    setBusinessSaving(true);
    try {
      const { data } = await api.put(`/admin/businesses/${businessId}`, form);
      setBusinesses((prev) => prev.map((b) => (b.id === businessId ? { ...b, ...data } : b)));
      setEditingBusiness(null);
    } finally {
      setBusinessSaving(false);
    }
  };

  const handleDeleteBusiness = async (businessId) => {
    const previous = businesses;
    setBusinesses((prev) => prev.filter((b) => b.id !== businessId));
    try {
      await api.delete(`/admin/businesses/${businessId}`);
    } catch (err) {
      console.error('Failed to delete business', err);
      setErrorMsg(err.response?.data?.message || 'Failed to delete business. Please try again.');
      setBusinesses(previous);
    }
  };


    const handleScheduleFollowUp = async (businessId, form) => {
    setFollowUpSaving(true);
    try {
      await api.post(`/admin/businesses/${businessId}/follow-ups`, form);
      setFollowUpBusiness(null);
      setFollowUpDetailKey((k) => k + 1);
      fetchBusinesses();
    } finally {
      setFollowUpSaving(false);
    }
  };

  const handleCompleteFollowUp = async (followUpId) => {
    await api.patch(`/admin/follow-ups/${followUpId}/complete`);
    setFollowUpDetailKey((k) => k + 1);
    fetchBusinesses();
  };

  const handleDeleteFollowUp = async (followUpId) => {
    if (!window.confirm('Delete this follow-up?')) return;
    await api.delete(`/admin/follow-ups/${followUpId}`);
    setFollowUpDetailKey((k) => k + 1);
    fetchBusinesses();
  };

  const handleCreateTemplate = async (form) => {
    setTemplateSaving(true);
    try {
      const { data } = await api.post('/admin/email-templates', form);
      setTemplates((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleUpdateTemplate = async (id, form) => {
    setTemplateSaving(true);
    try {
      const { data } = await api.put(`/admin/email-templates/${id}`, form);
      setTemplates((prev) => prev.map((t) => (t.id === id ? data : t)));
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    await api.delete(`/admin/email-templates/${id}`);
    setTemplates((prev) => prev.filter((t) => t.id !== id));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const allOnPageSelected = businesses.length > 0 && businesses.every((b) => selectedIds.includes(b.id));

  const toggleAllOnPage = () => {
    if (allOnPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !businesses.some((b) => b.id === id)));
    } else {
      setSelectedIds((prev) => {
        const merged = new Set(prev);
        businesses.forEach((b) => merged.add(b.id));
        return Array.from(merged);
      });
    }
  };

  const handleSend = async (subject, body) => {
  setSending(true);
  setSendError('');
  try {
    const { data } = await api.post('/admin/businesses/bulk-email', {
      business_ids: selectedIds,
      subject,
      body,
    });
    setShowCompose(false);
    setSendResult({ sent: data.sent, skipped: data.skipped || [] });
    setSelectedIds([]);
    fetchBusinesses(); // refresh so status badges (e.g. potential -> contacted) reflect the backend change
  } catch (err) {
    console.error('Bulk email failed', err);
    setSendError(
      err.response?.data?.message || 'Something went wrong sending these emails. Please try again.'
    );
  } finally {
    setSending(false);
  }
};

   return (
    <div className="bg-[#FDF5FF] -m-6 p-6 min-h-screen">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold  bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Businesses</h2>
        <div className="flex items-center gap-3">
      {allowTemplateManagement && (
  <button
    type="button"
    onClick={() => setShowTemplateManager(true)}
className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 hover:bg-purple-100"
  >
    Manage Email Templates
  </button>
)}
          <span className="text-sm text-gray-500">{meta.total.toLocaleString('en-IN')} total</span>
        </div>
      </div>

      {errorMsg && (
        <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {errorMsg}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><SearchIcon /></span>
          <input
            type="text"
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            placeholder="Search business name, CIN, address…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2focus:ring-purple-400"
          />
        </div>

        <select
          value={stateId}
          onChange={(e) => { setPage(1); setStateId(e.target.value); }}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
        >
          <option value="">All states</option>
          {states.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>

        <select
          value={status}
          onChange={(e) => { setPage(1); setStatus(e.target.value); }}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
        >
          <option value="">All statuses</option>
          {EMPLOYER_STATUSES.map((s) => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center justify-between mb-3 px-1">
        <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={allOnPageSelected}
            onChange={toggleAllOnPage}
            disabled={businesses.length === 0}
            className="w-4 h-4 rounded border-gray-300 hover:text-purple-600focus:ring-purple-400"
          />
          Select all on this page
        </label>

        {selectedIds.length > 0 && (
          <button
            type="button"
            onClick={() => setShowCompose(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90"
          >
            <MailIcon />
            Email {selectedIds.length} selected
          </button>
        )}
      </div>

      {sendError && (
        <div className="mb-3 px-4 py-2.5 rounded-lg bg-red-50 text-sm text-red-700">
          {sendError}
        </div>
      )}

      <div className="space-y-2">
        {loading && <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>}
        {!loading && businesses.length === 0 && (
          <div className="text-sm text-gray-400 py-8 text-center">No businesses match these filters.</div>
        )}
        {!loading && businesses.map((b) => (
          <BusinessRow
            key={b.id}
            b={b}
            checked={selectedIds.includes(b.id)}
            onToggle={toggleOne}
            onStatusChange={handleStatusChange}
            onOpenDetail={setSelectedBusinessId}
            onEdit={setEditingBusiness}
            onDelete={handleDeleteBusiness}
            allowDelete={allowDelete}
            onOpenEmailThread={setEmailThreadBusiness}
            onScheduleFollowUp={setFollowUpBusiness}
          />
        ))}
      </div>

      {meta.last_page > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">Page {page} of {meta.last_page}</span>
          <button
            onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
            disabled={page >= meta.last_page}
            className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

           {selectedBusinessId && (
        <BusinessDetailDrawer
          key={followUpDetailKey}
          businessId={selectedBusinessId}
          onClose={() => setSelectedBusinessId(null)}
          onStatusChange={handleStatusChange}
          onSchedule={setFollowUpBusiness}
          onComplete={handleCompleteFollowUp}
          onDelete={handleDeleteFollowUp}
        />
      )}

      {editingBusiness && (
        <EditBusinessModal
          business={editingBusiness}
          saving={businessSaving}
          onClose={() => setEditingBusiness(null)}
          onSave={handleSaveBusinessEdit}
        />
      )}

      {showCompose && (
        <ComposeEmailModal
          recipientCount={selectedIds.length}
          templates={templates}
          sending={sending}
          onClose={() => !sending && setShowCompose(false)}
          onSend={handleSend}
        />
      )}

      {allowTemplateManagement && showTemplateManager && (
        <TemplateManagerModal
          templates={templates}
          saving={templateSaving}
          onClose={() => setShowTemplateManager(false)}
          onCreate={handleCreateTemplate}
          onUpdate={handleUpdateTemplate}
          onDelete={handleDeleteTemplate}
        />
      )}

      {sendResult && (
        <SendResultsModal result={sendResult} onClose={() => setSendResult(null)} />
      )}

            {emailThreadBusiness && (
        <EmailChatDrawer business={emailThreadBusiness} onClose={() => setEmailThreadBusiness(null)} />
      )}

      {followUpBusiness && (
        <ScheduleFollowUpModal
          business={followUpBusiness}
          saving={followUpSaving}
          onClose={() => setFollowUpBusiness(null)}
          onSave={handleScheduleFollowUp}
        />
      )}
    </div>
  );
}

